import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from '@angular/cdk/scrolling';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ProgressBarModule } from 'primeng/progressbar';
import { TooltipModule } from 'primeng/tooltip';
import { AdminServerLogFilesService } from '../../../api/api-services/admin-server-log-files.service';
import { AdminServerLogFileService } from '../../../api/api-services/admin-server-log-file.service';
import {
  IServerLogFileInfoVm,
} from '../../../api/server-log-file-info-vm.model';
import {
  IFormattedLogLine,
  LogLineFormatterService,
} from '../../shared/services/log-line-formatter.service';

type LoadPhase = 'idle' | 'downloading' | 'analyzing' | 'ready';

@Component({
  selector: 'app-server-logs',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ProgressSpinnerModule,
    ProgressBarModule,
    TooltipModule,
    ScrollingModule,
  ],
  templateUrl: './server-logs.component.html',
  styleUrl: './server-logs.component.scss',
})
export class ServerLogsComponent implements OnInit {
  @ViewChild(CdkVirtualScrollViewport) viewport?: CdkVirtualScrollViewport;

  private _serverLogFilesService = inject(AdminServerLogFilesService);
  private _serverLogFileService = inject(AdminServerLogFileService);
  private _logLineFormatterService = inject(LogLineFormatterService);

  files: IServerLogFileInfoVm[] = [];
  serverFiles: IServerLogFileInfoVm[] = [];
  systemFiles: IServerLogFileInfoVm[] = [];
  selectedFile: IServerLogFileInfoVm | null = null;
  logLines: IFormattedLogLine[] = [];
  rawContent: string = '';

  loadingFiles = false;
  loadPhase: LoadPhase = 'idle';
  downloadProgress = 0;

  errorLineIndexes: number[] = [];
  warnLineIndexes: number[] = [];
  highlightedLine = -1;
  private _errorPosition = -1;
  private _warnPosition = -1;

  ngOnInit(): void {
    this.loadFilesList();
  }

  loadFilesList(): void {
    this.loadingFiles = true;
    this._serverLogFilesService.getFilesList().subscribe({
      next: (res) => {
        this.files = res?.files ?? [];
        // Older backend may not send "source" yet — treat missing as API logs.
        this.serverFiles = this.files.filter((f) => f.source !== 'system');
        this.systemFiles = this.files.filter((f) => f.source === 'system');
        this.loadingFiles = false;
        if (this.files.length > 0 && !this.selectedFile) {
          this.selectFile(this.files[0]);
        }
      },
      error: () => {
        this.loadingFiles = false;
      },
    });
  }

  selectFile(file: IServerLogFileInfoVm): void {
    if (this.loadPhase === 'downloading') return;
    this.selectedFile = file;
    this.loadFileContent(file);
  }

  isSelected(file: IServerLogFileInfoVm): boolean {
    // Names may repeat between directories (e.g. "syslog.log") — match source too.
    return (
      this.selectedFile?.fileName === file.fileName &&
      this.selectedFile?.source === file.source
    );
  }

  private loadFileContent(file: IServerLogFileInfoVm): void {
    this.logLines = [];
    this.rawContent = '';
    this.errorLineIndexes = [];
    this.warnLineIndexes = [];
    this.highlightedLine = -1;
    this._errorPosition = -1;
    this._warnPosition = -1;
    this.downloadProgress = 0;
    this.loadPhase = 'downloading';

    this._serverLogFileService
      .getWithProgress(file.fileName, file.source ?? 'server')
      .subscribe({
      next: (event) => {
        if (event.type === HttpEventType.DownloadProgress) {
          const total = event.total ?? file.sizeBytes;
          if (total > 0) {
            this.downloadProgress = Math.min(
              100,
              Math.round((event.loaded / total) * 100)
            );
          }
        } else if (event.type === HttpEventType.Response) {
          this.downloadProgress = 100;
          this.loadPhase = 'analyzing';
          this.rawContent = event.body?.logsData ?? '';

          // Give the browser one frame to paint the "analyzing" state
          // before the synchronous parse of a large file starts.
          setTimeout(() => this.analyzeContent());
        }
      },
      error: () => {
        this.loadPhase = 'idle';
      },
    });
  }

  private analyzeContent(): void {
    this.logLines = this._logLineFormatterService.formatContent(this.rawContent);

    this.errorLineIndexes = [];
    this.warnLineIndexes = [];
    this.logLines.forEach((line, index) => {
      if (line.level === 'ERROR') this.errorLineIndexes.push(index);
      else if (line.level === 'WARN') this.warnLineIndexes.push(index);
    });

    this.loadPhase = 'ready';
    setTimeout(() => this.viewport?.scrollToIndex(this.logLines.length - 1));
  }

  refreshContent(): void {
    if (this.selectedFile && this.loadPhase !== 'downloading') {
      this.loadFileContent(this.selectedFile);
    }
  }

  downloadFile(): void {
    if (!this.selectedFile || !this.rawContent) return;
    const blob = new Blob([this.rawContent], {
      type: 'text/plain;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = this.selectedFile.fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  navigateError(direction: 1 | -1): void {
    if (!this.errorLineIndexes.length) return;
    let position = this._errorPosition + direction;
    if (position >= this.errorLineIndexes.length) position = 0;
    if (position < 0) position = this.errorLineIndexes.length - 1;
    this._errorPosition = position;
    this.scrollToLine(this.errorLineIndexes[position]);
  }

  navigateWarn(direction: 1 | -1): void {
    if (!this.warnLineIndexes.length) return;
    let position = this._warnPosition + direction;
    if (position >= this.warnLineIndexes.length) position = 0;
    if (position < 0) position = this.warnLineIndexes.length - 1;
    this._warnPosition = position;
    this.scrollToLine(this.warnLineIndexes[position]);
  }

  errorCounter(): string {
    return this._errorPosition >= 0
      ? `${this._errorPosition + 1}/${this.errorLineIndexes.length}`
      : `${this.errorLineIndexes.length}`;
  }

  warnCounter(): string {
    return this._warnPosition >= 0
      ? `${this._warnPosition + 1}/${this.warnLineIndexes.length}`
      : `${this.warnLineIndexes.length}`;
  }

  private scrollToLine(index: number): void {
    this.highlightedLine = index;
    this.viewport?.scrollToIndex(Math.max(0, index - 5));
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  formatDate(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${mm}-${dd} ${hh}:${min}`;
  }
}
